import React, { useRef, useEffect, useState, useCallback, memo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Navbar,
  Nav,
  NavDropdown,
  Button,
  Modal,
  Form,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";
import { FaUserCircle, FaBell, FaUser, FaUpload } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import {
  signoutUser,
  updateUserInfo,
  deleteExistingUser,
} from "../redux/authSlice";
import { get, ref } from "firebase/database";
import { db } from "../common/firebase";
import Login from "../pages/Login";
import logo from "../images/logo1.png";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { getStorage } from "firebase/storage";

// Memoized helper function to prevent unnecessary recalculations
const getInitials = (name) => {
  if (!name) return "A";
  return name
    .split(" ")
    .map((part) => part[0]?.toUpperCase())
    .join("");
};

// Extracted as a separate component for better performance
const CustomTooltip = memo(({ text, children, placement = "bottom" }) => (
  <OverlayTrigger
    placement={placement}
    overlay={<Tooltip id={`tooltip-${placement}`}>{text}</Tooltip>}
  >
    {children}
  </OverlayTrigger>
));

// Extracted modals as separate components to reduce complexity
const ProfileModal = memo(
  ({
    showProfileModal,
    handleCloseModal,
    profileData,
    handleInputChange,
    isEditing,
    handleProfileEdit,
    handleProfileCancel,
    handleProfileUpdate,
    fileInputRef,
    handleImageUpload,
    setShowDeleteModal,
    nickname,
  }) => (
    <Modal show={showProfileModal} onHide={handleCloseModal}>
      <Modal.Header closeButton>
        <Modal.Title>{nickname}'s Profile</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div className="text-center ">
            <div
              className={`position-relative d-inline-block ${
                isEditing ? "cursor-pointer" : ""
              }`}
              style={{
                transition: "transform 0.2s ease-in-out",
              }}
              onClick={() => isEditing && fileInputRef.current?.click()}
              onMouseEnter={(e) => {
                if (isEditing) {
                  e.currentTarget.style.transform = "scale(1.02)";
                }
              }}
              onMouseLeave={(e) => {
                if (isEditing) {
                  e.currentTarget.style.transform = "scale(1)";
                }
              }}
            >
              {/* <div
              className="rounded-circle d-flex align-items-center justify-content-center shadow bg-light border border-white"
              style={{
                width: '120px',
                height: '120px',
                overflow: 'hidden',
                margin: '0 auto',
              }}
            >
              {profileData.profileImage ? (
                <div className="w-100 h-100 position-relative">
                  <img
                    src={profileData.profileImage}
                    alt="Profile"
                    className="img-fluid w-100 h-100"
                    style={{ objectFit: 'cover', objectPosition: 'center' }}
                    loading="lazy"
                  />
                  {isEditing && (
                    <div
                      className="position-absolute w-100 h-100 top-0 start-0 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 opacity-0 transition-opacity"
                      onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                      onMouseLeave={(e) => (e.currentTarget.style.opacity = '0')}
                    >
                      <span className="text-white fw-medium small">
                        Change Photo
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <FaUser size={40} className="text-secondary opacity-75" />
              )}
            </div> */}
              {isEditing && (
                <div
                  className="position-absolute bottom-0 end-0 rounded-circle d-flex align-items-center justify-content-center bg-primary shadow"
                  style={{
                    transform: "translate(25%, 25%)",
                    width: "32px",
                    height: "32px",
                    transition:
                      "transform 0.2s ease-in-out, background-color 0.2s ease-in-out",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform =
                      "translate(25%, 25%) scale(1.1)";
                    e.currentTarget.style.backgroundColor = "#0b5ed7";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translate(25%, 25%)";
                    e.currentTarget.style.backgroundColor = "#0d6efd";
                  }}
                >
                  <FaUpload color="white" size={14} />
                </div>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              className="d-none"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={!isEditing}
            />
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Nickname</Form.Label>
            <Form.Control
              type="text"
              name="nickname"
              value={profileData.nickname}
              onChange={handleInputChange}
              disabled={!isEditing}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Username</Form.Label>
            <Form.Control
              type="text"
              name="username"
              value={profileData.username}
              onChange={handleInputChange}
              disabled={!isEditing}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={profileData.email}
              disabled={true}
              readOnly
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Birth Date</Form.Label>
            <Form.Control
              type="date"
              name="birthDate"
              value={profileData.birthDate || ""}
              onChange={handleInputChange}
              disabled={!isEditing}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Account Created</Form.Label>
            <Form.Control
              type="text"
              value={
                profileData.createdAt
                  ? new Date(profileData.createdAt).toLocaleDateString()
                  : ""
              }
              disabled={true}
              readOnly
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        {!isEditing ? (
          <>
            <Button variant="primary" onClick={handleProfileEdit}>
              Edit
            </Button>
            <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
              Delete My Account
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={handleProfileCancel}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleProfileUpdate}>
              Update
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  )
);

const DeleteAccountModal = memo(
  ({
    showDeleteModal,
    handleCloseDeleteModal,
    deleteError,
    handleDeleteAccount,
    isLoading,
  }) => (
    <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
      <Modal.Header closeButton>
        <Modal.Title>Delete Account</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {deleteError && (
          <div className="alert alert-danger" role="alert">
            {deleteError}
          </div>
        )}
        <p>
          Are you sure you want to delete your account? This action cannot be
          undone.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleCloseDeleteModal}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleDeleteAccount}
          disabled={isLoading}
        >
          {isLoading ? "Deleting..." : "Delete My Account"}
        </Button>
      </Modal.Footer>
    </Modal>
  )
);

// Extracted navigation items to reduce complexity
const navLinks = [
  { path: "/home", label: "Home" },
  { path: "/poems", label: "Poems" },
  { path: "/narrative", label: "Narratives" },
  { path: "/about", label: "About" },
];

const Header = () => {
  const navbarRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);

  // Use memoized selectors for Redux state to prevent unnecessary re-renders
  const auth = useSelector((state) => state.auth);
  const { user } = auth;
  const nickname = user?.nickname || "Anonymous";

  const [expanded, setExpanded] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [profileData, setProfileData] = useState({
    username: "",
    email: "",
    birthDate: "",
    createdAt: "",
    nickname: "",
    // profileImage: ''
  });

  // Debounce function to limit rapid state changes
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // Memoize expensive operations using useCallback to prevent unnecessary re-renders
  const handleImageUpload = useCallback(
    async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Validation
      if (file.size > 5 * 1024 * 1024) {
        alert("Image size should be less than 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        alert("Please upload an image file");
        return;
      }

      try {
        setIsLoading(true);
        const storage = getStorage();

        // // Cleanup old image
        // if (profileData.profileImage) {
        //   try {
        //     const oldImageRef = storageRef(storage, profileData.profileImage);
        //     await deleteObject(oldImageRef);
        //   } catch (error) {
        //     console.log('No existing image found or error deleting:', error);
        //   }
        // }

        // Upload new image
        const timestamp = Date.now();
        const storageReference = storageRef(
          storage,
          `profile-images/${user.uid}_${timestamp}`
        );
        const snapshot = await uploadBytes(storageReference, file);
        // const downloadURL = await getDownloadURL(snapshot.ref);

        // Update user data
        await dispatch(
          updateUserInfo({
            ...profileData,
            // profileImage: downloadURL
          })
        ).unwrap();

        setProfileData((prev) => ({
          ...prev,
          // profileImage: downloadURL
        }));
      } catch (error) {
        console.error("Error in image upload:", error);
        alert("Failed to upload image");
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, profileData, user?.uid]
  );

  // Use memoized callback functions for event handlers
  const fetchProfileData = useCallback(async () => {
    if (user?.uid) {
      try {
        setIsLoading(true);
        const userRef = ref(db, `users/${user.uid}`);
        const snapshot = await get(userRef);

        if (snapshot.exists()) {
          const userData = snapshot.val();
          setProfileData({
            username: userData.username || "",
            nickname: userData.nickname || "",
            email: userData.email || "",
            birthDate: userData.birthDate || "",
            createdAt: userData.createdAt || "",
            // profileImage: userData.profileImage || ''
          });
        }
      } catch (error) {
        console.error("Error fetching profile data:", error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [user?.uid]);

  // Effect for profile image updates
  // useEffect(() => {
  //   if (user?.profileImage) {
  //     setProfileData(prev => ({
  //       ...prev,
  //       profileImage: user.profileImage
  //     }));
  //   }
  // }, [user?.profileImage]);

  // Effect for fetching profile data when modal opens
  useEffect(() => {
    if (showProfileModal) {
      fetchProfileData();
    }
  }, [showProfileModal, fetchProfileData]);

  // Handle clicking outside navbar to close it
  const handleClickOutside = useCallback((event) => {
    if (navbarRef.current && !navbarRef.current.contains(event.target)) {
      setExpanded(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  // Navigation handler with memoization
  const handleNavClick = useCallback(
    (path) => {
      setExpanded(false);
      navigate(path);
    },
    [navigate]
  );

  // Sign out handler with memoization
  const handleSignOut = useCallback(() => {
    dispatch(signoutUser())
      .unwrap()
      .then(() => navigate("/"))
      .catch((error) => console.error("Sign out error:", error));
  }, [dispatch, navigate]);

  // Profile edit handlers with memoization
  const handleProfileEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleProfileCancel = useCallback(() => {
    setIsEditing(false);
    fetchProfileData();
  }, [fetchProfileData]);

  const handleCloseModal = useCallback(() => {
    setIsEditing(false);
    setShowProfileModal(false);
  }, []);

  // Profile update handler with memoization and form validation
  const handleProfileUpdate = useCallback(() => {
    if (!user?.uid) return;

    // Basic validation
    if (!profileData.nickname.trim()) {
      alert("Nickname cannot be empty");
      return;
    }

    setIsLoading(true);
    dispatch(
      updateUserInfo({
        uid: user.uid,
        username: profileData.username,
        nickname: profileData.nickname,
        email: profileData.email,
        birthDate: profileData.birthDate,
        // profileImage: profileData.profileImage
      })
    )
      .unwrap()
      .then(() => {
        setIsEditing(false);
        setShowProfileModal(false);
      })
      .catch((error) => {
        console.error("Error updating profile:", error);
        alert("Failed to update profile");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, profileData, user?.uid]);

  // Form input handler with debouncing
  const handleInputChange = useCallback(
    debounce((e) => {
      const { name, value } = e.target;
      setProfileData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }, 300),
    []
  );

  // Account deletion handler with memoization
  const handleDeleteAccount = useCallback(async () => {
    if (!user?.uid) return;

    try {
      setIsLoading(true);
      await dispatch(deleteExistingUser(user.uid)).unwrap();
      setShowProfileModal(false);
      setShowDeleteModal(false);
      navigate("/");
    } catch (error) {
      let errorMessage = "An error occurred while deleting your account.";
      if (error.code === "auth/requires-recent-login") {
        errorMessage =
          "For security reasons, please log out and log in again before deleting your account.";
      }
      setDeleteError(errorMessage);
      console.error("Delete account error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, navigate, user?.uid]);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setDeleteError(null);
  }, []);

  // Memoize this calculation
  const initials = getInitials(nickname);
  // const userProfileImage = auth.user?.profileImage || '';
  // Navigation item rendering - extracted for cleaner code
  const renderNavLinks = useCallback(() => {
    return navLinks.map((link) => (
      <Nav.Link
        key={link.path}
        as={Link}
        to={link.path}
        onClick={() => handleNavClick(link.path)}
        className={location.pathname === link.path ? "nav-link-selected" : ""}
      >
        {link.label}
      </Nav.Link>
    ));
  }, [location.pathname, handleNavClick]);

  // Accessibility improvements
  const navbarId = "main-navbar";
  const dropdownId = "profile-dropdown";

  return (
    <>
      <Navbar
        ref={navbarRef}
        bg="light"
        variant="light"
        expand="lg"
        sticky="top"
        expanded={expanded}
        collapseOnSelect
        id={navbarId}
        aria-label="Main navigation"
      >
        <div className="container-fluid">
          <Navbar.Brand
            as={Link}
            to="/"
            onClick={() => handleNavClick("/home")}
          >
            <img
              src={logo}
              alt="Admin Portal Logo"
              width="120"
              height="50"
              className="d-inline-block align-top"
              loading="lazy"
            />
          </Navbar.Brand>

          <Navbar.Toggle
            aria-controls={navbarId}
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
          />

          <Navbar.Collapse id={navbarId}>
            <Nav className="mx-auto">{renderNavLinks()}</Nav>

            {user ? (
              <Nav>
                {/* <button 
                  className="me-2 bg-transparent border-0" 
                  onClick={() => handleNavClick('/notifications')}
                  aria-label="Notifications"
                >
                  <FaBell size={22} />
                </button> */}

                <NavDropdown
                  title={
                    <>
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            backgroundColor: "#6c757d", // or any theme-neutral color
                            color: "#fff",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "16px",
                            textTransform: "uppercase",
                          }}
                        >
                          {initials}
                        </div>
                      {/* {userProfileImage ? (
                        <img
                          src={userProfileImage}
                          alt={initials}
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            backgroundColor: "#6c757d", // or any theme-neutral color
                            color: "#fff",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "16px",
                            textTransform: "uppercase",
                          }}
                        >
                          {initials}
                        </div>
                      )} */}
                    </>
                  }
                  id={dropdownId}
                  align="end"
                  aria-label="User menu"
                >
                  <NavDropdown.ItemText style={{ fontWeight: 600 }}>
                    Hello {nickname}
                  </NavDropdown.ItemText>
                  <NavDropdown.Divider />

                  <NavDropdown.Item onClick={() => setShowProfileModal(true)}>
                    Profile
                  </NavDropdown.Item>

                  <NavDropdown.Item
                    as={Link}
                    to="/bookmarks"
                    onClick={() => handleNavClick("/bookmarks")}
                  >
                    My bookmarks
                  </NavDropdown.Item>
                   <NavDropdown.Item
                    as={Link}
                    to="/myInterests"
                    onClick={() => handleNavClick('/myInterests')}
                  >
                    My interests
                  </NavDropdown.Item>
{/*
                  <NavDropdown.Item
                    as={Link}
                    to="/suggest"
                    onClick={() => handleNavClick('/suggest')}
                  >
                    My suggestions
                  </NavDropdown.Item> */}

                  <NavDropdown.Item as={Link} to="/" onClick={handleSignOut}>
                    Logout
                  </NavDropdown.Item>
                </NavDropdown>
              </Nav>
            ) : (
              // <CustomTooltip text="Log in, My profile, My interests, My bookmarks, My suggestions, features are coming soon!">
              <div>
                <Button
                  // disabled
                  variant="light"
                  onClick={() => navigate("/LoginPage")}
                  className="p-2"
                  aria-label="Log in (coming soon)"
                >
                  <FaUserCircle size={30} />
                </Button>
              </div>
              // </CustomTooltip>
            )}
          </Navbar.Collapse>

          {/* Extracted modals as separate components */}
          <ProfileModal
            showProfileModal={showProfileModal}
            handleCloseModal={handleCloseModal}
            profileData={profileData}
            handleInputChange={handleInputChange}
            isEditing={isEditing}
            handleProfileEdit={handleProfileEdit}
            handleProfileCancel={handleProfileCancel}
            handleProfileUpdate={handleProfileUpdate}
            fileInputRef={fileInputRef}
            handleImageUpload={handleImageUpload}
            setShowDeleteModal={setShowDeleteModal}
            nickname={nickname}
          />

          <DeleteAccountModal
            showDeleteModal={showDeleteModal}
            handleCloseDeleteModal={handleCloseDeleteModal}
            deleteError={deleteError}
            handleDeleteAccount={handleDeleteAccount}
            isLoading={isLoading}
          />

          <Login show={showLogin} handleClose={() => setShowLogin(false)} />
        </div>
      </Navbar>
    </>
  );
};

export default memo(Header);
